import React, { createContext, useCallback, useEffect, useState } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { useAppSelector } from '../../store/reduxHooks';
import { useIsDesktop } from '../../utils/helpers';
import { intercome_id } from '../../settings';

// Intercom's script (about 230 KB) loads this long after the page, so it doesn't hold up the first view. Opening the
// chat or signing in before then is queued and happens once it has loaded
const INTERCOM_DELAY_MS = 4000;

// Unread Intercom messages, for the badge on the phone tab bar's Help tab
export const SupportUnreadContext = createContext(0);

const PrintSupport = ({ children }: { children: React.ReactNode }) => {
  const { boot, shutdown, show, update } = useIntercom();
  const { currentUser } = useAppSelector((state) => state.user);
  const { user_id } = currentUser;
  const isDesktop = useIsDesktop();
  // On phones the chat opens from the tab bar's Help tab; the floating bubble covered buttons there
  const hideDefaultLauncher = !isDesktop;

  useEffect(() => {
    update({ hideDefaultLauncher });
  }, [update, hideDefaultLauncher]);

  useEffect(() => {
    if (user_id) {
      setTimeout(() => {
        boot({
          userId: user_id?.toString(),
          hideDefaultLauncher,
          // Add other user details here
        });
      }, 500); // 500ms delay to ensure Intercom is ready
    }
    return () => {
      shutdown();
    };
    // hideDefaultLauncher is applied with update() above; re-booting on resize would reset the chat
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boot, shutdown, user_id]);

  const handleClick = useCallback(() => {
    show(); // This will show the Intercom Messenger
  }, [show]);

  return (
    <div>
      <button style={{ display: 'none' }} onClick={handleClick}></button>
      {children}
    </div>
  );
};

const PrintSupportWithIntercom = ({ children }: { children: React.ReactNode }) => {
  const [unread, setUnread] = useState(0);
  const isDesktop = useIsDesktop();

  return (
    <IntercomProvider
      appId={`${intercome_id}`}
      autoBoot
      initializeDelay={INTERCOM_DELAY_MS}
      autoBootProps={{ hideDefaultLauncher: !isDesktop }}
      onUnreadCountChange={setUnread}
    >
      <SupportUnreadContext.Provider value={unread}>
        <PrintSupport>{children}</PrintSupport>
      </SupportUnreadContext.Provider>
    </IntercomProvider>
  );
};

export default PrintSupportWithIntercom;
